import { DestroyRef, Injectable, effect, inject, signal, untracked } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { isAnessaEmail, RedSchedulerClaimDto } from '@anessa/shared-models';
import { MyAuthService } from '../../auth/auth.service';

/**
 * The signed-in staff member's RED scheduler claim (`~redclaim` /
 * `~redrelease` in the `~` dev menu): while it lasts, only the backend that
 * took it runs the RED scheduled jobs (see RedSchedulerClaimService on the
 * backend). Kept here so the dev commands and the app-wide claim banner share
 * one state. Asked once on sign-in for Anessa accounts, then set by the
 * commands' answers; cleared on its own at expiry, which the server applies
 * whatever happens to this app.
 */
@Injectable({ providedIn: 'root' })
export class RedSchedulerClaimService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(MyAuthService);
  private readonly base = '/api/red/scheduler-claim';
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;

  /** The claim in force when it is the signed-in user's; null otherwise. */
  readonly myClaim = signal<RedSchedulerClaimDto | null>(null);

  constructor() {
    effect(() => {
      if (isAnessaEmail(this.authService.ensuredUser()?.email)) untracked(() => this.refresh());
    });
    inject(DestroyRef).onDestroy(() => this.clearTimer());
  }

  refresh(): void {
    this.http.get<RedSchedulerClaimDto | null>(this.base).subscribe((claim) => this.set(claim));
  }

  /** `~redclaim`: the backend answering alone runs the RED jobs for a while; again extends it. */
  claim(): Observable<RedSchedulerClaimDto> {
    return this.http.post<RedSchedulerClaimDto>(this.base, {}).pipe(tap((claim) => this.set(claim)));
  }

  /** `~redrelease`: every backend runs the RED jobs again; null when nothing was claimed. */
  release(): Observable<RedSchedulerClaimDto | null> {
    return this.http
      .post<RedSchedulerClaimDto | null>(`${this.base}/release`, {})
      .pipe(tap(() => this.set(null)));
  }

  private set(claim: RedSchedulerClaimDto | null): void {
    this.clearTimer();
    const me = this.authService.ensuredUser()?.email?.toLowerCase();
    const mine = claim && me && claim.email?.toLowerCase() === me ? claim : null;
    const left = mine ? Date.parse(mine.expiresAt) - Date.now() : 0;
    this.myClaim.set(left > 0 ? mine : null);
    if (left > 0) this.expiryTimer = setTimeout(() => this.myClaim.set(null), left);
  }

  private clearTimer(): void {
    if (this.expiryTimer) clearTimeout(this.expiryTimer);
    this.expiryTimer = null;
  }
}
